import { useMemo } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { NotificationToast } from "../components/Generic/Toast/NotificationToast";
import { PRIVATE_ROUTES } from "../constants";
import { useAppState } from "../context/StateContext";
import { AutomaticSequencing } from "../models/automatic.sequencing";
import { Carton } from "../models/carton";
import { Error } from "../models/error";
import { PurchaseOrder, PurchaseOrderProduct } from "../models/purchase.order";
import { Filters, Searches } from "../models/shipeezi.table";
import CartonService from "../services/carton.service";
import PurchaseOrderService from "../services/purchase.order.service";
import { checkError } from "../util/checkError";
import { keysToCamel } from "../util/convertCamelCase";
import { PurchaseOrderKeys } from "../util/queries";
import useActionAutomaticSequence from "./useActionAutomaticSequence";
import { usePurchaseOrderContext } from "./usePurchaseOrderContext";

interface SavePurchaseOrderProps {
  id?: number;
  purchaseOrder: PurchaseOrder;
}
const CACHE_TIME = 60 * 60 * 1000; // 1 hour

const useActionPurchaseOrder = () => {
  const { currentUser, isFMVersion } = useAppState();
  const { t: translate } = useTranslation();
  const { purchaseOrder, setResponseError, setMilestoneDates, milestoneDates } =
    usePurchaseOrderContext();
  const { isAutomaticSequenceReference, increaseAutomaticSequence } = useActionAutomaticSequence();
  const history = useHistory();

  const queryClient = useQueryClient();

  const projects = useMemo(() => currentUser?.projects, [currentUser]);
  const defaultProjectId = useMemo(() => {
    if (projects && projects?.length) return Number(projects[0].id || 0);

    return 0;
  }, [projects]);

  const attributesPurchaseOrders = [
    "id",
    "companyId",
    "reference",
    "type",
    "productTotalQuantity",
    "date",
    "origin",
    "destination",
    "eta",
    "currentEta",
    "status",
    "performancePrediction",
    "fromIntegration",
    "unitQuantity",
    "supplierName",
  ];

  const includePurchaseOrders = JSON.stringify([
    { association: "milestones", include: [{ association: "milestone" }] },
    { association: "consignee", attributes: ["name"] },
  ]);

  const getAllPurchaseOrders = (
    page: number = 1,
    pageSize: number = 20,
    searchs?: Searches,
    filters?: Filters,
    attributes: string[] = attributesPurchaseOrders
  ) => {
    return useQuery({
      queryKey: PurchaseOrderKeys.getAllNetwork([
        page,
        pageSize,
        searchs,
        filters,
        currentUser?.companyId,
        attributes,
      ]),
      queryFn: async () => {
        const response = await PurchaseOrderService.getAllNetwork({
          page,
          pageSize,
          group: ["purchase_order.id"],
          companyId: currentUser?.companyId,
          include: includePurchaseOrders,
          attributes,
          searchByOr: searchs && searchs.length > 0 ? JSON.stringify(searchs) : "",
          ...filters,
        });

        if (response && response.data) return response.data;

        return {
          rows: [],
          count: 0,
        };
      },

      enabled: !!currentUser?.companyId,
      staleTime: 2 * 60 * 1000, // 2 minutes
      cacheTime: CACHE_TIME,
    });
  };

  const getPurchaseOrder = (id: number) => {
    return useQuery({
      queryKey: PurchaseOrderKeys.getById(id),
      queryFn: async () => {
        const response = await PurchaseOrderService.getById(id);

        if (response && response.data) {
          return response.data;
        }
      },
      enabled: !!id,
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000, // 2 minutes
      cacheTime: CACHE_TIME,
    });
  };

  const getPurchaseOrdersByCompanyId = (
    setPurchaseOrders: (purchaseOrders: PurchaseOrderProduct[]) => void,
    setTotalItems: (value: number) => void,
    page: number = 1,
    pageSize: number = 20,
    search: string = "",
    filterSearch: string = ""
  ) => {
    return useQuery(
      [
        "purchase.order.getByCompanyId",
        currentUser?.companyId,
        page,
        pageSize,
        search,
        filterSearch,
      ],
      () =>
        PurchaseOrderService.getByCompanyId(currentUser?.companyId ?? 0, {
          page,
          pageSize,
          search,
          filterSearch,
        }),
      {
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          if (response && response.data && response.data.data) {
            setPurchaseOrders(keysToCamel(response.data.data));
            setTotalItems(response.data.total);
          } else {
            setPurchaseOrders([]);
            setTotalItems(0);
          }
        },
      }
    );
  };

  const getPurchaseOrderStatuses = () => {
    return useQuery({
      queryKey: ["purchase.order.statuses"],
      queryFn: async () => {
        const response = await PurchaseOrderService.getStatuses();

        if (response?.data) {
          return response.data;
        }

        return [];
      },

      staleTime: 2 * 60 * 1000, // 2 minutes
      cacheTime: CACHE_TIME,
    });
  };

  const useSavePurchaseOrder = (sequence?: AutomaticSequencing) => {
    return useMutation(
      ["purchaseOrder.save"],
      ({ id, purchaseOrder }: SavePurchaseOrderProps) => {
        if (id)
          return PurchaseOrderService.update(id, {
            ...purchaseOrder,
            companyId: currentUser?.companyId,
            supplierId: purchaseOrder.supplier?.id,
          });

        return PurchaseOrderService.create({
          ...purchaseOrder,
          companyId: currentUser?.companyId,
          projectId: isFMVersion ? purchaseOrder.projectId : defaultProjectId,
          supplierId: purchaseOrder.supplier?.id,
        });
      },
      {
        onSuccess: () => {
          NotificationToast.success(translate("operationPerformedSuccessfully"));
          if (
            sequence &&
            purchaseOrder?.reference &&
            sequence.id &&
            isAutomaticSequenceReference(sequence, purchaseOrder.reference)
          )
            increaseAutomaticSequence({
              sequence,
              value: purchaseOrder.reference,
            });
          queryClient.invalidateQueries({ queryKey: [PurchaseOrderKeys.all, "network"] });
          queryClient.removeQueries({
            queryKey: PurchaseOrderKeys.getById(purchaseOrder?.id),
          });
          PurchaseOrderKeys.getById(purchaseOrder?.id);

          history.push(PRIVATE_ROUTES.ORDERS);
        },
        onError: (error: AxiosError<Error>) => {
          if (error && error.response) {
            if (error.response.data.field !== "unknown") setResponseError(error.response.data);
            else checkError(error, translate("anErrorOccuredTryAgain"));
          }
        },
      }
    );
  };

  const useDeletePurchaseOrder = () => {
    return useMutation(["purchaseOrder.delete"], (id: number) => PurchaseOrderService.delete(id), {
      onSuccess: (_) => {
        NotificationToast.success(translate("itemRemovedSuccessfully"));
        history.push(PRIVATE_ROUTES.ORDERS);
      },
    });
  };

  const getOrderMilestoneDates = (companyId?: number) => {
    return useQuery({
      queryKey: PurchaseOrderKeys.getMilestones([companyId]),
      queryFn: async () => {
        const response = await PurchaseOrderService.getMilestoneDates({ companyId });

        if (response) {
          if (response?.data && response?.data instanceof Array) {
            setMilestoneDates(response?.data);
          }

          return response?.data;
        }
      },
      enabled: !!companyId && !milestoneDates?.length,
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000, // 2 minutes
      cacheTime: CACHE_TIME,
    });
  };

  const getCartonsFromOrder = (
    purchaseOrderId: number,
    setCartons: (cartons: Carton[]) => void
  ) => {
    return useQuery(
      ["order.cartons.get"],
      () =>
        CartonService.getAll({
          include: JSON.stringify([
            {
              association: "cartonProducts",
              required: true,
              where: { purchaseOrderId },
              include: [
                { association: "product", required: true },
                { association: "purchaseOrderProduct", required: true, paranoid: false },
              ],
            },
            {
              association: "shipment",
              where: {
                valid: true,
              },
              attributes: ["statusShipment", "containerId", "id"],
            },
          ]),
        }),
      {
        enabled: !!currentUser?.companyId && !!purchaseOrderId,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          if (response && response.data) {
            setCartons(response.data);
          }
        },
      }
    );
  };

  const useCancelOrders = () => {
    return useMutation(
      ["orde.cancel"],
      (orderIds: number[]) => PurchaseOrderService.cancelOrders(orderIds),
      {
        onSuccess: () => {
          NotificationToast.success(translate("orderCancelledSuccessfully"));
          queryClient.invalidateQueries(["purchase.order.list"]);
        },
      }
    );
  };

  return {
    getAllPurchaseOrders,
    getPurchaseOrder,
    useSavePurchaseOrder,
    useDeletePurchaseOrder,
    getPurchaseOrdersByCompanyId,
    getPurchaseOrderStatuses,
    getOrderMilestoneDates,
    getCartonsFromOrder,
    useCancelOrders,
  };
};

export default useActionPurchaseOrder;
